import {useMemo} from 'react';

import type {PostImage, PostType} from 'mattermost-redux/types/posts';
import {ErrorBoundary} from 'components/error_boundary/error_boundary';
import messageHtmlToComponent from 'utils/message_html_to_component';
import {formatText, type Team, type MentionKey, type TextFormattingOptions} from 'utils/text_formatting';
import PostEditedIndicator from '../post_view/post_edited_indicator';
import type EmojiMap from 'utils/emoji_map';

type Props = {

    /*
     * Whether or not to do Markdown rendering
     */
    enableFormatting?: boolean;

    /*
     * Whether or not this text is part of the RHS
     */
    isRHS?: boolean;

    /*
     * The text to be rendered
     */
    message: string;

    /*
     * Any additional text formatting options to be used
     */
    options: Partial<TextFormattingOptions>;

    /**
     * Whether or not to proxy image URLs
     */
    proxyImages?: boolean;

    /**
     * Any extra props that should be passed into the image component
     */
    // eslint-disable-next-line @typescript-eslint/ban-types
    imageProps?: object;

    /**
     * prop for passed down to image component for dimensions
     */
    imagesMetadata?: Record<string, PostImage>;

    /**
     * Whether or not to place the LinkTooltip component inside links
     */
    hasPluginTooltips?: boolean;

    /**
     * Post id prop passed down to markdown image
     */
    postId?: string;

    /**
     * When the post is edited this is the timestamp it happened at
     */
    editedAt?: number;

    channelId?: string;

    /**
     * Post id prop passed down to markdown image
     */
    postType?: PostType;

    disableHashtags?: boolean;

    autolinkedUrlSchemes: string[];
    siteURL: string;
    additionalSiteURLs?: string;
    mentionKeys: MentionKey[];
    channelNamesMap: Record<string, string>;
    team: Team | undefined;
    minimumHashtagLength: number;
    managedResourcePaths: string[];
    emojiMap: EmojiMap;
};

const DEFAULT_OPTIONS: Partial<TextFormattingOptions> = {};
const DEFAULT_IMAGES_METADATA: Record<string, PostImage> = {};

type RawMessageProps = Pick<Props, 'message' | 'postId' | 'editedAt'>;

const RawMessage = ({message, postId, editedAt}: RawMessageProps) => {
    return (
        <span>
            {message}
            <PostEditedIndicator
                postId={postId}
                editedAt={editedAt}
            />
        </span>
    );
};

const Markdown = (props: Props) => {
    const {
        postId = '',
        editedAt = 0,
        message,
        enableFormatting,
        isRHS = false,
        imageProps,
        imagesMetadata = DEFAULT_IMAGES_METADATA,
        hasPluginTooltips,
        channelId,
        postType,
        options = DEFAULT_OPTIONS,
        disableHashtags = false,
        autolinkedUrlSchemes,
        siteURL,
        additionalSiteURLs,
        mentionKeys,
        channelNamesMap,
        proxyImages,
        team,
        minimumHashtagLength,
        managedResourcePaths,
        emojiMap,
    } = props;

    const {mentionHighlight, disableGroupHighlight, emojiOverlayPlacement} = options;
    const htmlFormattedText = useMemo(() => {
        return formatText(message, {
            ...options,
            autolinkedUrlSchemes,
            siteURL,
            additionalSiteURLs,
            mentionKeys,
            atMentions: true,
            channelNamesMap,
            proxyImages,
            team,
            disableHashtags,
            minimumHashtagLength,
            managedResourcePaths,
            editedAt,
            postId,
        }, emojiMap);
    }, [
        additionalSiteURLs,
        autolinkedUrlSchemes,
        channelNamesMap,
        disableHashtags,
        editedAt,
        emojiMap,
        managedResourcePaths,
        mentionKeys,
        message,
        minimumHashtagLength,
        options,
        postId,
        proxyImages,
        siteURL,
        team,
    ]);

    if (message === '' || htmlFormattedText === '' || !enableFormatting) {
        return <RawMessage message={message} postId={postId} editedAt={editedAt} />;
    }

    return messageHtmlToComponent(htmlFormattedText, isRHS, {
        imageProps,
        imagesMetadata,
        hasPluginTooltips,
        postId,
        channelId,
        postType,
        mentionHighlight,
        disableGroupHighlight,
        editedAt,
        emojiOverlayPlacement,
        disableHashtags,
    });
};

const MarkdownWithErrorBoundary = (props: Props) => {
    const renderFallback = () => {
        return (
            <RawMessage
                message={props.message}
                postId={props.postId}
                editedAt={props.editedAt}
            />
        );
    };

    return (
        <ErrorBoundary fallbackRender={renderFallback}>
            <Markdown {...props} />
        </ErrorBoundary>
    );
};

export default MarkdownWithErrorBoundary;
